// "I've paid" on a student's pending booking (PA-1). The student taps it and picks one payment
// screenshot; the booking is marked payment sent and stays pending until the instructor confirms.
// Once payment is sent the same button replaces the screenshot. The instructor is emailed once.
import React from 'react';
import { Btn } from './index';
import { PAYMENT_PROOF_ALLOWED, PAYMENT_PROOF_TYPES, paymentProofProblem, uploadPaymentProof } from '../lib/privateFiles';
import { palette as COLORS, type } from '../design';

const FONT = "'Plus Jakarta Sans', sans-serif";

const SENT_MESSAGE = 'Payment sent. Your instructor confirms your booking once they see your payment.';
const REPLACED_MESSAGE = 'Screenshot replaced. Your booking still shows payment sent.';
const FAILED_MESSAGE = 'We could not send your screenshot. Check your connection and try again.';

export function MarkPaidButton({ booking, onMarked }) {
  const inputRef = React.useRef(null);
  const hintId = React.useId();
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);

  if (!booking || booking.status !== 'pending') return null;
  const alreadySent = Boolean(booking.paymentSentAt);

  const pick = () => {
    setError(null);
    inputRef.current?.click();
  };

  const onFile = async (event) => {
    const file = event.target.files?.[0];
    // Cleared so picking the same file again still fires a change.
    event.target.value = '';
    if (!file) return;

    const problem = paymentProofProblem(file);
    if (problem) {
      setNotice(null);
      setError(problem);
      return;
    }

    setSending(true);
    setError(null);
    try {
      const result = await uploadPaymentProof(booking.id, file);
      setNotice(alreadySent ? REPLACED_MESSAGE : SENT_MESSAGE);
      onMarked?.(result?.booking);
    } catch (err) {
      setNotice(null);
      setError(err?.data?.message || FAILED_MESSAGE);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, maxWidth: 320 }}>
      <Btn size="sm" variant={alreadySent ? 'ghost' : 'primary'} onClick={pick} disabled={sending}>
        {sending ? 'Sending…' : alreadySent ? 'Replace screenshot' : "I've paid"}
      </Btn>
      <input
        ref={inputRef}
        type="file"
        accept={PAYMENT_PROOF_TYPES.join(',')}
        aria-label="Payment screenshot"
        aria-describedby={hintId}
        onChange={onFile}
        style={{ display: 'none' }}
      />
      <span id={hintId} style={{ fontFamily: FONT, fontSize: type.caption, color: COLORS.driftwood, textAlign: 'right' }}>
        Attach your payment screenshot: {PAYMENT_PROOF_ALLOWED}
      </span>
      {error && (
        <span role="alert" style={{ fontFamily: FONT, fontSize: type.label, color: COLORS.errorRed, textAlign: 'right', lineHeight: 1.5 }}>{error}</span>
      )}
      {notice && (
        <span role="status" style={{ fontFamily: FONT, fontSize: type.label, color: COLORS.sageDark, textAlign: 'right', lineHeight: 1.5 }}>{notice}</span>
      )}
    </div>
  );
}
